import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { useCompanyVerifiers } from 'services/api/queries';
import { EPDVerifierModel } from 'types/types';
import { Option } from 'types/types';
import { getValidationIssueMessage } from 'util/compilerValidation';

import EpdVerifierSelectorDialog from './EpdVerifierSelectorDialog';
import { ActionState } from 'services/EpdClient';

interface EpdVerifierSelectorProps {
  epdId: string;
  epdVersionId: string;
  epdVerifier: EPDVerifierModel | null;
  onChangeVerifier(propertyName: string, verifier: EPDVerifierModel): void;
  errors: any;
  state: ActionState;
}

const EpdVerifierSelector: React.FunctionComponent<EpdVerifierSelectorProps> = ({
  epdId,
  epdVersionId,
  epdVerifier,
  onChangeVerifier,
  errors,
  state,
}) => {
  const { companyId } = useContext(CompanyContext);
  const verifiers = useCompanyVerifiers(companyId ?? '').data;
  const hasAccessToVerifierEdit = state === ActionState.Enabled;
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);
  const { epdVerifier: epdVerifierError, epdVerifierUnavailable } = errors || {};

  const isVerifierSelectionEnabled =
    (!epdVerifier ||
      !!epdVerifierUnavailable ||
      verifiers?.some((verifier) => verifier.contactUserId === epdVerifier?.contactUserId)) &&
    hasAccessToVerifierEdit;

  const selectedVerifier = !!epdVerifier
    ? ({
        label: epdVerifier.displayName,
        value: epdVerifier.id,
      } as Option)
    : undefined;

  return (
    <>
      <CompilerSelectControlledComponent
        label="EPD Verifier"
        tooltip="The third-party verifier responsible for verifying this EPD. The EPD Owner can manage available verifiers and add new verifiers."
        name="epdVerifier"
        options={verifiers?.map(
          (verifier) =>
            ({
              value: verifier.id,
              label: verifier.displayName,
            } as Option)
        )}
        value={selectedVerifier}
        onChanged={onChangeVerifier}
        placeholder="Select"
        isClearable={true}
        disabled={!isVerifierSelectionEnabled}
        error={epdVerifierError || epdVerifierUnavailable}
        required
      />
      {epdVerifierUnavailable && (
        <span style={{ paddingTop: 10, paddingBottom: 10, color: 'red' }}>
          {getValidationIssueMessage(epdVerifierUnavailable)}
        </span>
      )}
      {hasAccessToVerifierEdit && <WizardAddButton label="+ Add EPD Verifier" onClick={() => setShowSelectDialog(true)} />}
      <EpdVerifierSelectorDialog
        epdId={epdId}
        epdVersionId={epdVersionId}
        epdVerifier={epdVerifier}
        onHide={() => setShowSelectDialog(false)}
        isOpened={showSelectDialog}
      />
    </>
  );
};

export default EpdVerifierSelector;
